/**
 * RSpin — RedClub-branded spin loader.
 *
 * A spinning ring with the "R" wordmark in the center. Use this anywhere
 * you'd otherwise show "Loading..." text. Three sizes:
 *
 *   <RSpin />               // default md (32px)
 *   <RSpin size="sm" />     // 20px — inline next to buttons
 *   <RSpin size="lg" />     // 56px — full-page or tab-level loaders
 *
 * Pass `label` to render an aria-live announcement underneath; pass
 * `className` on a wrapping element for layout positioning.
 */
const SIZE_MAP = {
  sm: { box: 20, stroke: 2, font: 8 },
  md: { box: 32, stroke: 2.5, font: 12 },
  lg: { box: 56, stroke: 3, font: 22 },
};

export function RSpin({ size = "md", label = "", className = "", color = "#002FA7", "data-testid": testId = "r-spin" }) {
  const dim = SIZE_MAP[size] || SIZE_MAP.md;
  return (
    <div className={`inline-flex flex-col items-center justify-center gap-2 ${className}`} data-testid={testId}>
      <div className="relative" style={{ width: dim.box, height: dim.box }}>
        <svg
          viewBox="0 0 50 50"
          width={dim.box}
          height={dim.box}
          className="animate-spin"
          style={{ animationDuration: "0.9s" }}
        >
          {/* faint background ring */}
          <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeOpacity="0.12" strokeWidth={dim.stroke} />
          {/* spinning arc */}
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke={color}
            strokeWidth={dim.stroke}
            strokeLinecap="round"
            strokeDasharray="80 200"
            strokeDashoffset="0"
          />
        </svg>
        {/* centered R mark — counter-spins to stay upright */}
        <div
          className="absolute inset-0 flex items-center justify-center font-bold select-none"
          style={{
            color,
            fontSize: dim.font,
            fontFamily: "var(--font-heading, ui-sans-serif, system-ui)",
            letterSpacing: "-0.04em",
          }}
        >
          R
        </div>
      </div>
      {label && (
        <p className="text-xs text-slate-500 font-medium" aria-live="polite">
          {label}
        </p>
      )}
    </div>
  );
}

/**
 * Full-section loading placeholder — drop into any panel that needs to
 * communicate "fetching from server, give it a moment". Renders a tall
 * centered RSpin with optional context label.
 */
export function RSpinBlock({ label = "Loading", height = 220, "data-testid": testId = "r-spin-block" }) {
  return (
    <div
      className="flex items-center justify-center w-full"
      style={{ minHeight: height }}
      data-testid={testId}
    >
      <RSpin size="lg" label={label} />
    </div>
  );
}

export default RSpin;
